import React from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { BoxWrapper } from "../../../admin-section/trips/drawer-contents/TripDrawer";
import { usePost, usePut } from "../../../hooks/api-call";
import useCustomToast from "../../../hooks/use-toast";
import * as Yup from "yup";
import { useYupValidationResolver } from "../../../hooks/use-validation-resolver";
import {
  createEmailSchema,
  createGstSchema,
  createPanSchema,
  pincodeSchema,
} from "../../../hooks/validationSchemas";
import NewAddressDetails from "../../trucks/drawer-forms/NewAddressDetails";

const validationSchema = Yup.object().shape({
  category: Yup.string(),
  buyerName: Yup.string(),
  contactNo: Yup.string().matches(/^[0-9]{10}$/, "Invalid contact number"),
  emailId: createEmailSchema(),
  gstNo: createGstSchema(),
  panNo: createPanSchema(),
  pincode: pincodeSchema,
});

const BuyerDrawer = ({
  setIsDrawerOpen,
  setPagination,
  setSelectedBuyerForEdit,
  defaultValues,
}) => {
  const { showSuccessToast } = useCustomToast();
  const { postData } = usePost();
  const { putData } = usePut();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm({
    defaultValues,
    resolver: useYupValidationResolver(validationSchema),
    mode: "onBlur",
  });

  const onClose = () => {
    setIsDrawerOpen(false);
    setSelectedBuyerForEdit(false);
    reset({});
  };

  const handleSubmitForm = async (data) => {
    if (defaultValues?.buyerId) {
      await putData(data, `buyers/${defaultValues.buyerId}`);
      showSuccessToast("Success", "Buyer updated successfully");
    } else {
      await postData(data, "buyer");
      showSuccessToast("Success", "Buyer added successfully");
    }
    setPagination((prev) => ({ ...prev, pageIndex: 1 }));
    onClose();
  };

  return (
    <Drawer anchor="right" open onClose={onClose} sx={{ "& .MuiDrawer-paper": { width: "60%" } }}>
      <Box sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <Typography variant="h6" fontWeight="bold">
            {defaultValues?.buyerId ? "Edit Buyer" : "New Buyer"}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <form onSubmit={handleSubmit(handleSubmitForm)} noValidate>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              color: "#333",
              p: 1,
              mb: 2,
              borderLeft: "4px solid #1976d2",
              bgcolor: "#f0f0f0",
              pl: 2,
            }}
          >
            Buyer Information
          </Typography>

          <BoxWrapper>
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      value={field.value || ""}
                      label="Category"
                      defaultValue="Select Category"
                      error={Boolean(errors.category)}
                      helperText={errors.category?.message}
                    >
                      <MenuItem value="" disabled>
        Select Category
      </MenuItem>
                      <MenuItem value="manufacture">Manufacture</MenuItem>
                      <MenuItem value="dealer">Dealer</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="buyerName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Name"
                      placeholder="Enter buyer name"
                      error={Boolean(errors.buyerName)}
                      helperText={errors.buyerName?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="contactNo"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Contact Number"
                      placeholder="Enter contact number"
                      error={Boolean(errors.contactNo)}
                      helperText={errors.contactNo?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="emailId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Email"
                      placeholder="Enter email"
                      error={Boolean(errors.emailId)}
                      helperText={errors.emailId?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="gstNo"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="GST Number"
                      placeholder="Enter GST number"
                      error={Boolean(errors.gstNo)}
                      helperText={errors.gstNo?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="panNo"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="PAN Number"
                      placeholder="Enter PAN number"
                      error={Boolean(errors.panNo)}
                      helperText={errors.panNo?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </BoxWrapper>

          <BoxWrapper>
            <NewAddressDetails control={control} errors={errors} />
          </BoxWrapper>

          <Box display="flex" justifyContent="flex-end" mt={3} position="relative">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isDirty || !isValid || isSubmitting}
              sx={{ width: "200px", position: "absolute", bottom: 5, right: 5 }}
              onClick={() => document.activeElement.blur()}
            >
              {defaultValues?.buyerId ? "Update Buyer" : "Add Buyer"}
            </Button>
          </Box>
        </form>
      </Box>
    </Drawer>
  );
};

export default BuyerDrawer;